// This is a temporary solution for i18n support across remotasks/dashboard
// Proper i18n support should be implemented in the future if we have more use cases

// eslint-disable-next-line restrict-imports/restrict-imports
import { useTranslation } from 'remotasks/i18n';
import { Languages } from '@scale/shared/types/demographics';
import { Kbd, Text } from '@scale/scaleui-radix';

const EN_TEXTS = {
  // PromptInput
  'submit-message': 'Submit Message',
  'submit-hint': (
    <Text as="span">
      Press <Kbd size="1">Shift</Kbd> + <Kbd size="1">Enter</Kbd> to submit your message.
    </Text>
  ),
  'skip-message': 'Skip Message',

  // MultiTurnContinue
  'continue-hint': 'Write a follow-up prompt to continue the chat thread',
  'continue-hint-can-end':
    'Write a follow-up prompt to continue the chat thread, or end your session',
  'end-session': 'Are you sure you want to end the session?',
  suggestions: 'Suggestions:',

  // ModelResponseEditor
  'edit-step': 'Edit Step',
  'finish-editing': 'Finish Editing',
  'get-auto-critique': 'Get Critique',
  'fetching-auto-critique': 'Fetching Critique...',
  editor: 'Editor',
  'markdown-preview': 'Markdown Preview',
  'diff-view': 'Diff View',

  // ModelResponseSelector hints
  'select-response-hint': 'Click to select a response and continue',
  response: 'Response',

  // ModelResponseRanking hints
  'responses-ranking-drop-zone-hint': 'Drop candidates here',

  // Generic step submission hint
  'save-and-continue': 'Save and Continue',
};

const KO_TEXTS: typeof EN_TEXTS = {
  // PromptInput
  'submit-message': '메시지 제출',
  'submit-hint': (
    <Text as="span">
      <Kbd size="1">Shift</Kbd> + <Kbd size="1">Enter</Kbd> 키를 눌러 메시지를 제출하세요.
    </Text>
  ),

  'skip-message': '메시지 건너뛰기',

  // MultiTurnContinue
  'continue-hint': '대화를 이어서 작성하세요',
  'continue-hint-can-end': '대화를 이어서 작성하거나 세션을 종료하세요',
  'end-session': '세션 종료',
  suggestions: '제안:',

  // ModelResponseEditor
  'edit-step': '단계 편집',
  'finish-editing': '편집 완료',
  'get-auto-critique': '자동 평가 받기',
  'fetching-auto-critique': '자동 평가 중...',
  editor: '편집기',
  'markdown-preview': '마크다운 미리보기',
  'diff-view': '차이 보기',

  // ModelResponseSelector hints
  response: '응답',
  'select-response-hint': '가장 알맞은 답을 선택하고 계속하려면 클릭하세요',

  // ModelResponseRanking hints
  'responses-ranking-drop-zone-hint': 'Drop candidates here',

  // Generic step submission hint
  'save-and-continue': '저장하고 계속',
};

export type Key = keyof typeof EN_TEXTS;

const LocaleTextMapping: Record<string, typeof EN_TEXTS> = {
  [Languages.English]: EN_TEXTS,
  [Languages.Korean]: KO_TEXTS,
};

export const useTrans = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const texts = Object.keys(LocaleTextMapping).includes(language)
    ? LocaleTextMapping[language]
    : EN_TEXTS;

  return {
    t: <T extends Key>(key: T): (typeof EN_TEXTS)[T] => {
      return texts[key];
    },
  };
};
